import type { ReactNode } from "react"

import { useLocale, useTranslations } from "next-intl"

import { Button, Cents, Chip, HairlinePlusIcon, Text } from "@/components/ui"
import type { Locale } from "@/i18n/locales"
import { formatMoney } from "@/lib/money"
import { paysMonthly, type InstallmentView } from "@/lib/money/price-view"

interface PaymentHeaderProps {
  plan: InstallmentView
  badge?: ReactNode
  caption: ReactNode
}

export const PaymentHeader = ({ plan, badge, caption }: PaymentHeaderProps) => {
  const t = useTranslations("pdp")
  const locale = useLocale() as Locale
  const monthly = paysMonthly(plan)
  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex min-w-0 flex-wrap items-start gap-x-100">
        <span className="flex items-start gap-x-100">
          <Text as="span" family="text" tone="brand" className="text-price-sm @3xl:text-price-md">
            <Cents>{formatMoney(plan.amounts[0]!, locale)}</Cents>
          </Text>
          <Text as="span" family="text" tone="brand" className="text-little @3xl:text-little-lg mt-200">
            {t(monthly ? "priceHero.planTimesMonthly" : "priceHero.planTimes", { count: plan.count })}
          </Text>
        </span>
        {badge}
      </span>
      <Text as="span" family="text" className="text-little @3xl:text-little-lg text-fg-heading">
        {caption}
      </Text>
    </span>
  )
}

export const SecuredPaymentHeader = ({ plan, showBadge = true }: { plan: InstallmentView; showBadge?: boolean }) => {
  const t = useTranslations("pdp")
  const tBnpl = useTranslations("marketplace.bnpl")
  const locale = useLocale() as Locale
  const downPayment = plan.downPayment && Number(plan.downPayment.amount) > 0 ? plan.downPayment : null
  return (
    <PaymentHeader
      plan={plan}
      badge={
        // A short secured plan at 0% (4 quincenas) is the interest-free option, not the "more time" one.
        showBadge &&
        (plan.interestFree ? (
          <Chip tone="nointerest" size="little" className="mt-150">
            {tBnpl("noInterest")}
          </Chip>
        ) : (
          <Chip tone="moretime" size="little" className="mt-150">
            {t("priceHero.moreTime")}
          </Chip>
        ))
      }
      caption={downPayment ? t("priceHero.securedDownPaymentOf", { amount: formatMoney(downPayment, locale) }) : t("priceHero.securedNoDownPayment")}
    />
  )
}

interface PlanHeaderProps {
  canOpen: boolean
  open: boolean
  onToggle: () => void
  glyphRotation: string
  children: ReactNode
}

export const PlanHeader = ({ canOpen, open, onToggle, glyphRotation, children }: PlanHeaderProps) => {
  if (!canOpen) return <div className="flex items-start justify-between gap-300 px-375 pt-375 pb-400 @3xl:p-500">{children}</div>
  return (
    <Button
      variant="quiet"
      fullWidth
      onClick={onToggle}
      aria-expanded={open}
      className="rounded-7-5x @3xl:rounded-10x h-auto items-start justify-between gap-300 px-375 pt-375 pb-400 text-left @3xl:p-500"
    >
      {children}
      <span className={`text-fg-brand duration-moderate ease-productive flex size-6 shrink-0 items-center justify-center transition-transform ${glyphRotation}`}>
        <HairlinePlusIcon size={16} aria-hidden />
      </span>
    </Button>
  )
}
